import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

// Import translation files
import deTranslations from "../locales/de/common.json";
import enTranslations from "../locales/en/common.json";
import esTranslations from "../locales/es/common.json";
import frTranslations from "../locales/fr/common.json";
import hiTranslations from "../locales/hi/common.json";
import idTranslations from "../locales/id/common.json";
import itTranslations from "../locales/it/common.json";
import jaTranslations from "../locales/ja/common.json";
import koTranslations from "../locales/ko/common.json";
import nlTranslations from "../locales/nl/common.json";
import plTranslations from "../locales/pl/common.json";
import ptTranslations from "../locales/pt/common.json";
import ruTranslations from "../locales/ru/common.json";
import trTranslations from "../locales/tr/common.json";
import viTranslations from "../locales/vi/common.json";
import zhTranslations from "../locales/zh/common.json";

// Import download-apps translations
import deDownloadApps from "../locales/de/download-apps.json";
import enDownloadApps from "../locales/en/download-apps.json";
import deCreatorCompetition from "../locales/de/creator-competition.json";
import enCreatorCompetition from "../locales/en/creator-competition.json";
import esCreatorCompetition from "../locales/es/creator-competition.json";
import esDownloadApps from "../locales/es/download-apps.json";
import frCreatorCompetition from "../locales/fr/creator-competition.json";
import frDownloadApps from "../locales/fr/download-apps.json";
import hiCreatorCompetition from "../locales/hi/creator-competition.json";
import hiDownloadApps from "../locales/hi/download-apps.json";
import idCreatorCompetition from "../locales/id/creator-competition.json";
import idDownloadApps from "../locales/id/download-apps.json";
import itCreatorCompetition from "../locales/it/creator-competition.json";
import itDownloadApps from "../locales/it/download-apps.json";
import jaCreatorCompetition from "../locales/ja/creator-competition.json";
import jaDownloadApps from "../locales/ja/download-apps.json";
import koCreatorCompetition from "../locales/ko/creator-competition.json";
import koDownloadApps from "../locales/ko/download-apps.json";
import nlCreatorCompetition from "../locales/nl/creator-competition.json";
import nlDownloadApps from "../locales/nl/download-apps.json";
import plCreatorCompetition from "../locales/pl/creator-competition.json";
import plDownloadApps from "../locales/pl/download-apps.json";
import ptCreatorCompetition from "../locales/pt/creator-competition.json";
import ptDownloadApps from "../locales/pt/download-apps.json";
import ruCreatorCompetition from "../locales/ru/creator-competition.json";
import ruDownloadApps from "../locales/ru/download-apps.json";
import trCreatorCompetition from "../locales/tr/creator-competition.json";
import trDownloadApps from "../locales/tr/download-apps.json";
import viCreatorCompetition from "../locales/vi/creator-competition.json";
import viDownloadApps from "../locales/vi/download-apps.json";
import zhCreatorCompetition from "../locales/zh/creator-competition.json";
import zhDownloadApps from "../locales/zh/download-apps.json";

// Define supported languages - expandable to 10 languages
export const SUPPORTED_LANGUAGES = {
  en: { code: "en", name: "English", nativeName: "English" },
  es: { code: "es", name: "Spanish", nativeName: "Español" },
  fr: { code: "fr", name: "French", nativeName: "Français" },
  de: { code: "de", name: "German", nativeName: "Deutsch" },
  it: { code: "it", name: "Italian", nativeName: "Italiano" },
  pt: { code: "pt", name: "Portuguese", nativeName: "Português" },
  ja: { code: "ja", name: "Japanese", nativeName: "日本語" },
  ko: { code: "ko", name: "Korean", nativeName: "한국어" },
  ru: { code: "ru", name: "Russian", nativeName: "Русский" },
  hi: { code: "hi", name: "Hindi", nativeName: "हिन्दी" },
  nl: { code: "nl", name: "Dutch", nativeName: "Nederlands" },
  pl: { code: "pl", name: "Polish", nativeName: "Polski" },
  zh: { code: "zh", name: "Simplified Chinese", nativeName: "简体中文" },
  vi: { code: "vi", name: "Vietnamese", nativeName: "Tiếng Việt" },
  tr: { code: "tr", name: "Turkish", nativeName: "Türkçe" },
  id: { code: "id", name: "Indonesian", nativeName: "Bahasa Indonesia" },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

// Resources object with current translations
const resources = {
  en: {
    common: enTranslations,
    "download-apps": enDownloadApps,
    "creator-competition": enCreatorCompetition,
  },
  es: {
    common: esTranslations,
    "download-apps": esDownloadApps,
    "creator-competition": esCreatorCompetition,
  },
  fr: {
    common: frTranslations,
    "download-apps": frDownloadApps,
    "creator-competition": frCreatorCompetition,
  },
  de: {
    common: deTranslations,
    "download-apps": deDownloadApps,
    "creator-competition": deCreatorCompetition,
  },
  it: {
    common: itTranslations,
    "download-apps": itDownloadApps,
    "creator-competition": itCreatorCompetition,
  },
  pt: {
    common: ptTranslations,
    "download-apps": ptDownloadApps,
    "creator-competition": ptCreatorCompetition,
  },
  ru: {
    common: ruTranslations,
    "download-apps": ruDownloadApps,
    "creator-competition": ruCreatorCompetition,
  },
  ja: {
    common: jaTranslations,
    "download-apps": jaDownloadApps,
    "creator-competition": jaCreatorCompetition,
  },
  hi: {
    common: hiTranslations,
    "download-apps": hiDownloadApps,
    "creator-competition": hiCreatorCompetition,
  },
  pl: {
    common: plTranslations,
    "download-apps": plDownloadApps,
    "creator-competition": plCreatorCompetition,
  },
  zh: {
    common: zhTranslations,
    "download-apps": zhDownloadApps,
    "creator-competition": zhCreatorCompetition,
  },
  id: {
    common: idTranslations,
    "download-apps": idDownloadApps,
    "creator-competition": idCreatorCompetition,
  },
  tr: {
    common: trTranslations,
    "download-apps": trDownloadApps,
    "creator-competition": trCreatorCompetition,
  },
  nl: {
    common: nlTranslations,
    "download-apps": nlDownloadApps,
    "creator-competition": nlCreatorCompetition,
  },
  vi: {
    common: viTranslations,
    "download-apps": viDownloadApps,
    "creator-competition": viCreatorCompetition,
  },
  ko: {
    common: koTranslations,
    "download-apps": koDownloadApps,
    "creator-competition": koCreatorCompetition,
  },
};

i18n
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    // Default language
    fallbackLng: "en",

    // Available languages
    supportedLngs: Object.keys(SUPPORTED_LANGUAGES),

    // Language detection options
    detection: {
      // Detection order and options
      order: ["localStorage", "navigator", "htmlTag"],

      // Cache user language on
      caches: ["localStorage"],

      // Only detect languages that are in the supportedLngs list
      checkWhitelist: true,
    },

    // Resources (translations)
    resources,

    // Default namespace
    defaultNS: "common",

    // Interpolation options
    interpolation: {
      escapeValue: false, // React already does escaping
    },

    // React i18next options
    react: {
      // Bind events to components
      bindI18n: "languageChanged",
      // Auto-sync the i18n state with React
      useSuspense: false,
    },

    // Development options
    debug: process.env.NODE_ENV === "development",

    // Namespace separator
    nsSeparator: ":",

    // Key separator
    keySeparator: ".",
  });

export default i18n;
